import { useAction, useAtom } from '@reatom/npm-react'
import styled from 'styled-components'
import { useMemo } from 'use-memo-one'

import { NumericFormat } from '@blade/std-lib'
import { NumericInput } from '@blade/ui-kit'
import { CurrencyModel } from '@root/entities/currency'
import { CurrencyIcon } from '@root/entities/currency/ui'

import type { DistributeFundsModel } from '../../../atoms'
import type { DistributeTargetModel } from '../../../atoms/reatom-distribute-target'
import type { ClassNameProps } from '@root/shared/react'

type WalletDestinationAmountInputProps = {
  target: DistributeTargetModel
  model: DistributeFundsModel
} & ClassNameProps

export const WalletDestinationAmountInput: React.FC<
  WalletDestinationAmountInputProps
> = ({ target, model }) => {
  const [rawAmount] = useAtom(target.amountAtom.raw)
  const [resolvedAmount] = useAtom(target.resolvedAmount)
  const setRawAmount = useAction(target.setRawAmountAction)
  const [source] = useAtom(target.inputSource)
  const [currency] = useAtom(model.currency)

  const nativeCurrency = CurrencyModel.nativeOf(target.wallet.network)

  const amountToRender = useMemo(() => {
    if (source === 'amount') {
      return rawAmount
    }
    return NumericFormat.formatPreset(resolvedAmount, 'full')
  }, [source, resolvedAmount, rawAmount])

  return (
    <Root>
      <Measurer>
        <span>{amountToRender}</span>
        <Input
          min={0}
          value={amountToRender}
          onChange={(e) => setRawAmount(e.target.value)}
        />
      </Measurer>
      <CurrencyIcon currency={currency ?? nativeCurrency} size={12} />
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  grid-template-columns: auto auto;
  align-items: center;
  gap: 4px;
`
const Input = styled(NumericInput)`
  position: absolute;
  inset: 0;
  height: 100%;
  width: 100%;
  border: none;
  padding: 0;
  background: transparent;
  color: var(--text-12);

  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
`
const Measurer = styled.div`
  position: relative;
  display: inline-block;
  min-width: 1ch;
  font-size: 12px;
  font-weight: 500;
  height: 16px;
  color: transparent;
`
